// components/viewport/starfield.ts
//
// The parallax star engine, shared by the two backgrounds that each carried their own copy of it.
// The ALGORITHM is shared; the SETTINGS are not. Each background keeps the look it was tuned for,
// because a starfield config is a product decision about one surface, not a technical detail:
// Studio fades its stars out entirely below zoom 0.1 to hide a rendering defect that shows at that
// zoom (`83de708f0`, 2025-10-03), and the org-chart viewport never had that constraint.

export interface StarfieldLayer {
  count: number;
  size: number;
  color: string;
  speed: number;
}

export interface StarfieldConfig {
  layers: StarfieldLayer[];
  /** Global alpha for a given zoom. Owned by the surface, never averaged between surfaces. */
  opacityAt: (zoomLevel: number) => number;
}

const TILE_WIDTH = 2048;
const TILE_HEIGHT = 2048;

/**
 * The Studio editor canvas (InpaintCanvas), by far the most-used of the two surfaces, hence the
 * default. Its opacity curve reaches 0 at or below zoom 0.1 ON PURPOSE — the stars are hidden to
 * cover a display defect that appears when zoomed that far out. Do not give it a floor.
 */
export const STUDIO_STARFIELD: StarfieldConfig = {
  layers: [
    { count: 600, size: 1, color: 'rgba(255,255,255,0.7)', speed: 0.1 },
    { count: 200, size: 2, color: 'rgba(255,255,255,0.5)', speed: 0.2 },
    { count: 50, size: 3, color: 'rgba(255,255,255,0.3)', speed: 0.3 },
  ],
  opacityAt: (zoomLevel) => Math.min(1, Math.max(0, (zoomLevel - 0.1) / 0.2)),
};

/** The infinite viewport behind the organisation chart and teams pages. */
export const VIEWPORT_STARFIELD: StarfieldConfig = {
  layers: [
    { count: 620, size: 1, color: 'rgba(255,255,255,0.64)', speed: 0.08 },
    { count: 220, size: 1.8, color: 'rgba(255,255,255,0.42)', speed: 0.16 },
    { count: 54, size: 2.8, color: 'rgba(255,255,255,0.24)', speed: 0.28 },
  ],
  opacityAt: (zoomLevel) => Math.min(1, Math.max(0.2, (zoomLevel - 0.04) / 0.22)),
};

/** Builds the offscreen star tiles, one per parallax layer. Call once per mounted background. */
export const createStarfieldTiles = (config: StarfieldConfig = STUDIO_STARFIELD): HTMLCanvasElement[] =>
  config.layers.map((layer) => {
    const tileCanvas = document.createElement('canvas');
    tileCanvas.width = TILE_WIDTH;
    tileCanvas.height = TILE_HEIGHT;
    const context = tileCanvas.getContext('2d');

    if (context) {
      context.clearRect(0, 0, TILE_WIDTH, TILE_HEIGHT);
      context.fillStyle = layer.color;
      for (let index = 0; index < layer.count; index += 1) {
        context.beginPath();
        context.arc(Math.random() * TILE_WIDTH, Math.random() * TILE_HEIGHT, layer.size / 2, 0, 2 * Math.PI);
        context.fill();
      }
    }

    return tileCanvas;
  });

/**
 * Sizes the canvas for the device pixel ratio and runs the parallax render loop until the
 * returned disposer is called. The caller owns only the viewport state source. `resize` resizes
 * and redraws synchronously, for a caller that must paint in the same frame its host moved.
 */
export const runStarfield = (options: {
  canvas: HTMLCanvasElement;
  tiles: () => HTMLCanvasElement[];
  width: number;
  height: number;
  readViewportState: () => { panOffset: { x: number; y: number }; zoomLevel: number };
  config?: StarfieldConfig;
}): (() => void) & { resize: (width: number, height: number) => void } => {
  const { canvas, tiles, readViewportState } = options;
  const config = options.config ?? STUDIO_STARFIELD;
  const context = canvas.getContext('2d');
  if (!context) return Object.assign(() => {}, { resize: () => {} });

  const devicePixelRatio = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
  let displayWidth = 0;
  let displayHeight = 0;

  const applySize = (width: number, height: number) => {
    // A not-yet-measured host (0 or 1px) must keep its CSS sizing until the real size lands.
    if (width <= 1 || height <= 1) return false;
    displayWidth = width;
    displayHeight = height;
    const targetWidth = Math.round(displayWidth * devicePixelRatio);
    const targetHeight = Math.round(displayHeight * devicePixelRatio);
    if (canvas.width !== targetWidth) canvas.width = targetWidth;
    if (canvas.height !== targetHeight) canvas.height = targetHeight;
    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;
    return true;
  };
  applySize(options.width, options.height);

  const drawFrame = (panOffset: { x: number; y: number }, zoomLevel: number) => {
    if (displayWidth <= 1 || displayHeight <= 1) return;
    context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
    context.clearRect(0, 0, displayWidth, displayHeight);

    const viewportCenterX = displayWidth / 2;
    const viewportCenterY = displayHeight / 2;
    const opacity = config.opacityAt(zoomLevel);
    // Fully faded out (Studio below zoom 0.1) means nothing to paint, not a transparent pass.
    if (opacity <= 0) return;

    config.layers.forEach((layer, index) => {
      const tile = tiles()[index];
      if (!tile) return;

      const speed = layer.speed;
      const scale = (1 * (1 - speed)) + (zoomLevel * speed);
      const worldCenterX = (viewportCenterX - panOffset.x) / zoomLevel;
      const worldCenterY = (viewportCenterY - panOffset.y) / zoomLevel;
      const tx = viewportCenterX - (worldCenterX * speed * scale);
      const ty = viewportCenterY - (worldCenterY * speed * scale);
      const scaledTileWidth = TILE_WIDTH * scale;
      const scaledTileHeight = TILE_HEIGHT * scale;
      const startX = tx % scaledTileWidth - scaledTileWidth;
      const startY = ty % scaledTileHeight - scaledTileHeight;
      const tilesX = Math.ceil(displayWidth / scaledTileWidth) + 2;
      const tilesY = Math.ceil(displayHeight / scaledTileHeight) + 2;

      context.save();
      context.globalAlpha = opacity;
      for (let row = 0; row < tilesY; row += 1) {
        for (let col = 0; col < tilesX; col += 1) {
          context.drawImage(
            tile,
            startX + col * scaledTileWidth,
            startY + row * scaledTileHeight,
            scaledTileWidth,
            scaledTileHeight
          );
        }
      }
      context.restore();
    });
  };

  let animationFrameId = 0;
  let previousState = readViewportState();
  let needsRedraw = true;

  const renderLoop = () => {
    const currentState = readViewportState();
    const zoomChanged = Math.abs(currentState.zoomLevel - previousState.zoomLevel) > 0.0001;
    const panChanged =
      Math.abs(currentState.panOffset.x - previousState.panOffset.x) > 0.1 ||
      Math.abs(currentState.panOffset.y - previousState.panOffset.y) > 0.1;

    if (needsRedraw || zoomChanged || panChanged) {
      drawFrame(currentState.panOffset, currentState.zoomLevel);
      previousState = currentState;
      needsRedraw = false;
    }

    animationFrameId = requestAnimationFrame(renderLoop);
  };

  renderLoop();
  return Object.assign(() => cancelAnimationFrame(animationFrameId), {
    resize: (width: number, height: number) => {
      if (!applySize(width, height)) return;
      previousState = readViewportState();
      drawFrame(previousState.panOffset, previousState.zoomLevel);
      needsRedraw = false;
    },
  });
};
